import type { EncryptedRecoveryPackage, V2KeyContext, V2Keys } from "./keys";
import { base64Decode, base64Encode } from "./encoding";

export type PrivateAccountEntry = {
  id: string;
  label: string;
  walletAddress: string;
  walletDerived: boolean;
  backup?: string;
  subwallet?: string;
};

type StoragePort = Pick<Storage, "getItem" | "setItem">;
const copy = (keys: V2Keys): V2Keys => ({
  ...keys,
  rootSeed: keys.rootSeed.slice(),
  scanKey: keys.scanKey.slice(),
});
const wipe = (keys: V2Keys) => {
  keys.rootSeed.fill(0);
  keys.scanKey.fill(0);
};

// Persist only the established encrypted recovery format, not arbitrary input
// fields or plaintext key material. Addresses/names remain local metadata.
const encryptedBackup = (serialized: string): string => {
  if (serialized.length > 16_384) throw new Error("Recovery file is too large");
  const input = JSON.parse(serialized) as EncryptedRecoveryPackage;
  if (
    !input ||
    input.version !== 2 ||
    input.kdf !== "PBKDF2-SHA256" ||
    input.iterations !== 600_000 ||
    ![
      input.walletAddress,
      input.salt,
      input.nonce,
      input.ciphertext,
      input.contextHash,
    ].every((value) => typeof value === "string" && value.length > 0)
  ) {
    throw new Error("Invalid encrypted recovery file");
  }
  for (const [value, length] of [
    [input.salt, 16],
    [input.nonce, 12],
    [input.ciphertext, 48],
    [input.contextHash, 32],
  ] as const) {
    const decoded = base64Decode(value);
    if (decoded.length !== length || base64Encode(decoded) !== value)
      throw new Error("Invalid encrypted recovery file");
  }
  return JSON.stringify({
    version: 2,
    walletAddress: input.walletAddress,
    kdf: input.kdf,
    iterations: input.iterations,
    salt: input.salt,
    nonce: input.nonce,
    ciphertext: input.ciphertext,
    contextHash: input.contextHash,
  });
};

/** Local catalogue, not a server-side wallet mapping or a substitute for backups. */
export class PrivateAccountCatalog {
  private entries = new Map<string, PrivateAccountEntry>();
  private secrets = new Map<string, { keys: V2Keys; context: V2KeyContext }>();
  private saved = new Set<string>();
  private persisted = new Set<string>();
  private writable = true;
  private observedStorage: string | null = null;
  private conflictingStorage = false;
  private readonly storageKey: string;
  private readonly deployment: Pick<V2KeyContext, "chainId" | "proxyAddress">;

  constructor(
    deployment: Pick<V2KeyContext, "chainId" | "proxyAddress">,
    private readonly storage?: StoragePort
  ) {
    this.deployment = { ...deployment };
    this.storageKey = `redacted-v2-accounts:${deployment.chainId}:${deployment.proxyAddress}`;
    if (!storage) return;
    try {
      const raw = storage.getItem(this.storageKey);
      this.observedStorage = raw;
      if (!raw) return;
      if (raw.length > 1_000_000) throw new Error("Invalid account list");
      const parsed = JSON.parse(raw);
      if (
        parsed.version !== 1 ||
        !Array.isArray(parsed.accounts) ||
        parsed.accounts.length > 50
      )
        throw new Error("Invalid account list");
      const recovered = new Map<string, PrivateAccountEntry>();
      for (const entry of parsed.accounts) {
        if (
          typeof entry.id !== "string" ||
          !entry.id ||
          entry.id.length > 128 ||
          typeof entry.label !== "string" ||
          !entry.label.trim() ||
          entry.label.length > 40 ||
          typeof entry.walletAddress !== "string" ||
          entry.walletAddress.length > 128 ||
          typeof entry.walletDerived !== "boolean" ||
          recovered.has(entry.id)
        )
          throw new Error("Invalid saved account");
        const backup =
          entry.backup === undefined
            ? undefined
            : encryptedBackup(entry.backup);
        if (!backup && !entry.walletDerived)
          throw new Error("Missing encrypted backup");
        if (
          backup &&
          JSON.parse(backup).walletAddress.toLowerCase() !==
            entry.walletAddress.toLowerCase()
        )
          throw new Error("Recovery backup belongs to another account");
        recovered.set(entry.id, {
          id: entry.id,
          label: entry.label,
          walletAddress: entry.walletAddress,
          walletDerived: entry.walletDerived,
          backup,
        });
      }
      this.entries = recovered;
      this.saved = new Set(recovered.keys());
      this.persisted = new Set(recovered.keys());
    } catch {
      // Do not overwrite an unreadable existing catalogue with an empty one.
      this.writable = false;
    }
  }

  remember(
    keys: V2Keys,
    context: V2KeyContext,
    options: {
      walletDerived?: boolean;
      backup?: string;
      subwallet?: string;
    } = {}
  ): string {
    if (
      context.chainId !== this.deployment.chainId ||
      context.proxyAddress !== this.deployment.proxyAddress
    )
      throw new Error("Account belongs to another deployment");
    const id = keys.recoveryId;
    if (!id || id.length > 128)
      throw new Error("Invalid private account identifier");
    if (!this.entries.has(id) && this.entries.size >= 50)
      throw new Error("Too many saved accounts");
    const previous = this.entries.get(id);
    if (
      previous &&
      previous.walletAddress.toLowerCase() !==
        context.walletAddress.toLowerCase()
    )
      throw new Error("Account identity does not match its saved context");
    const backup =
      options.backup === undefined
        ? previous?.backup
        : encryptedBackup(options.backup);
    if (
      backup &&
      JSON.parse(backup).walletAddress.toLowerCase() !==
        context.walletAddress.toLowerCase()
    )
      throw new Error("Recovery backup belongs to another account");
    this.entries.set(id, {
      id,
      label: previous?.label ?? `Private Account ${this.entries.size + 1}`,
      walletAddress: context.walletAddress,
      walletDerived: previous?.walletDerived || options.walletDerived || false,
      backup,
      subwallet: options.subwallet ?? previous?.subwallet,
    });
    const old = this.secrets.get(id);
    const owned = copy(keys);
    if (old) wipe(old.keys);
    this.secrets.set(id, { keys: owned, context: { ...context } });
    if (!this.persist()) this.saved.delete(id);
    return id;
  }

  /** A stale tab must reload before saving, never replace a newer account list. */
  get storageConflict(): boolean {
    return this.conflictingStorage;
  }

  get(id: string): PrivateAccountEntry {
    const entry = this.entries.get(id);
    if (!entry) throw new Error("Unknown private account");
    return { ...entry };
  }

  unlocked(id: string): { keys: V2Keys; context: V2KeyContext } | undefined {
    const entry = this.secrets.get(id);
    return entry && { keys: copy(entry.keys), context: { ...entry.context } };
  }

  list(connectedWallet?: string) {
    return [...this.entries.values()].map((entry) => ({
      id: entry.id,
      label: entry.label,
      subwallet: entry.subwallet,
      saved: this.saved.has(entry.id),
      unlock: this.secrets.has(entry.id)
        ? ("session" as const)
        : entry.walletDerived &&
            (!entry.backup ||
              connectedWallet?.toLowerCase() ===
                entry.walletAddress.toLowerCase())
          ? ("wallet" as const)
          : ("backup" as const),
    }));
  }

  rename(id: string, name: string): void {
    const label = name.trim();
    if (!label || label.length > 40)
      throw new Error("Use a name between 1 and 40 characters");
    const previous = this.get(id);
    this.entries.set(id, { ...previous, label });
    if (!this.persist() && (this.persisted.has(id) || this.storageConflict)) {
      this.entries.set(id, previous);
      throw new Error(
        this.storageConflict
          ? "Private Account list changed in another tab. Keep your recovery backup and reload before saving changes."
          : "Could not save the account name in this browser"
      );
    }
  }

  remove(id: string): void {
    const entry = this.get(id);
    if (!entry.backup && !entry.walletDerived)
      throw new Error("Back up this account before removing it");
    const previousEntries = this.entries;
    this.entries = new Map(this.entries);
    this.entries.delete(id);
    if (!this.persist() && (this.persisted.has(id) || this.storageConflict)) {
      this.entries = previousEntries;
      throw new Error(
        this.storageConflict
          ? "Private Account list changed in another tab. Keep your recovery backup and reload before saving changes."
          : "Could not remove the saved account from this browser"
      );
    }
    const unlocked = this.secrets.get(id);
    if (unlocked) wipe(unlocked.keys);
    this.secrets.delete(id);
    this.saved.delete(id);
  }

  lock(): void {
    for (const entry of this.secrets.values()) wipe(entry.keys);
    this.secrets.clear();
    for (const [id, entry] of this.entries) {
      if (!entry.walletDerived && !entry.backup) {
        // An unfinished independent account cannot be unlocked once its only
        // in-memory keys are erased. It has never been persisted.
        this.entries.delete(id);
        this.saved.delete(id);
      }
    }
  }

  private persist(): boolean {
    if (!this.storage || !this.writable) return false;
    try {
      // localStorage has no compare-and-swap: this detects an observed stale
      // snapshot, but is not a cross-tab transaction lock. Never merge away an
      // account or revive a removed backup after another tab changes the list.
      if (this.storage.getItem(this.storageKey) !== this.observedStorage) {
        this.conflictingStorage = true;
        this.writable = false;
        this.saved.clear();
        return false;
      }
      const accounts = [...this.entries.values()]
        .filter((entry) => entry.backup || entry.walletDerived)
        .map(({ subwallet: _privateAddress, ...entry }) => entry);
      const serialized = JSON.stringify({ version: 1, accounts });
      this.storage.setItem(this.storageKey, serialized);
      this.observedStorage = serialized;
      this.saved = new Set(accounts.map((entry) => entry.id));
      this.persisted = new Set(accounts.map((entry) => entry.id));
      return true;
    } catch {
      // Session access and downloadable backups must survive denied/quota storage.
      return false;
    }
  }
}
